import { ChangeEvent, FC, memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Radio, RadioGroup, TableCell, TableFooter, TableRow, useTheme } from "@mui/material";
import { IResultItem } from "../../types";
import getStyles from "./styles";

type Props = {
  options: IResultItem[];
  selected: string | number;
  resultFamilyIndex: number;
  onChange: (luminaire: IResultItem) => void;
};

const Selection: FC<Props> = ({ options, selected, resultFamilyIndex, onChange }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme).Selection, [theme]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const nextSelection = options.find((lum) => lum.name === event.target.value);
      if (nextSelection) onChange(nextSelection);
    },
    [onChange, options]
  );

  return (
    <TableFooter>
      <TableRow sx={styles.row}>
        <TableCell sx={styles.label}>{t("Select")}</TableCell>
        <TableCell colSpan={options.length} sx={styles.cells}>
          <RadioGroup name="luminaire" value={selected} onChange={handleChange} sx={styles.group}>
            {options.map((lum) => (
              <Radio key={lum.name} value={lum.name} sx={styles.radio} />
            ))}
          </RadioGroup>
        </TableCell>
      </TableRow>
    </TableFooter>
  );
};

const areOptionsEqual = (prevOptions: IResultItem[], nextOptions: IResultItem[]) =>
  prevOptions.length === nextOptions.length && prevOptions.every((option, index) => option === nextOptions[index]);

export default memo(Selection, (prevProps, nextProps) => {
  return prevProps.selected === nextProps.selected && prevProps.onChange === nextProps.onChange && areOptionsEqual(prevProps.options, nextProps.options);
});
